import CodeBlock from "../components/CodeBlock";
import FAQItems from "../components/FAQItems";
import ScreenshotFrame from "../../components/ScreenshotFrame";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const CARD_BORDER = "rgba(255,255,255,0.04)";

function H2({ id, children }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: "clamp(22px, 3vw, 28px)",
        color: TEXT,
        margin: "48px 0 16px",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: 19,
        color: TEXT,
        margin: "32px 0 12px",
        letterSpacing: "-0.005em",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>
      {children}
    </p>
  );
}

function Li({ children }) {
  return (
    <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>
      {children}
    </li>
  );
}

function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}

function A({ href, children }) {
  const isExternal = href.startsWith("http");
  return (
    <a
      href={href}
      {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}
    >
      {children}
    </a>
  );
}

function Code({ children }) {
  return (
    <code style={{ background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4, fontFamily: "var(--mono)", fontSize: 13, color: TEXT }}>
      {children}
    </code>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderLeft: `3px solid ${TEAL}`,
        fontFamily: "var(--display)",
        fontSize: 20,
        fontWeight: 600,
        color: TEXT,
        lineHeight: 1.4,
        fontStyle: "normal",
      }}
    >
      {children}
    </blockquote>
  );
}

function InlineCTA({ children, href, label }) {
  return (
    <div
      style={{
        margin: "40px 0",
        padding: "24px 28px",
        borderRadius: 14,
        background: TEAL_DIM,
        border: "1px solid rgba(78,205,196,0.25)",
      }}
    >
      <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT, margin: "0 0 12px", fontWeight: 600 }}>
        {children}
      </p>
      <a
        href={href}
        style={{ color: TEAL, fontFamily: "var(--body)", fontSize: 15, fontWeight: 700, textDecoration: "none" }}
      >
        {label} &#8594;
      </a>
    </div>
  );
}

function Callout({ kind = "info", title, children }) {
  const palette =
    kind === "warn"
      ? { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.25)", titleColor: "#f87171" }
      : { bg: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.25)", titleColor: "#a78bfa" };
  return (
    <div
      style={{
        margin: "24px 0 32px",
        padding: "18px 22px",
        borderRadius: 12,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <div
        style={{
          fontFamily: "var(--display)",
          fontSize: 13,
          fontWeight: 700,
          color: palette.titleColor,
          marginBottom: 8,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>
      <div style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT_DIM, lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}

function StepHeader({ id, num, title, time }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", margin: "40px 0 16px" }}>
      <h3
        id={id}
        style={{
          fontFamily: "var(--display)",
          fontWeight: 700,
          fontSize: "clamp(18px, 2.4vw, 22px)",
          color: TEXT,
          margin: 0,
          letterSpacing: "-0.005em",
          lineHeight: 1.3,
          flex: "1 1 auto",
        }}
      >
        <span style={{ color: TEAL, fontFamily: "var(--mono)", fontSize: "0.78em", fontWeight: 700 }}>
          STEP {num}:
        </span>
        {" "}
        {title}
      </h3>
      {time && (
        <span
          aria-label={`Estimated time: ${time}`}
          style={{
            background: "rgba(255,255,255,0.04)",
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: 999,
            padding: "4px 12px",
            fontFamily: "var(--mono)",
            fontSize: 12,
            color: TEXT_DIM,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {time}
        </span>
      )}
    </div>
  );
}

// ── HowTo schema step list (canonical manual provisioning path) ──
const howToSteps = [
  {
    name: "Sign in to ads.openai.com",
    text: "Sign in to ads.openai.com with the account that has admin access to your ad account. The OpenAI Ads platform is distinct from platform.openai.com (where chat completion keys are issued) and from the OpenAI consumer ChatGPT.",
  },
  {
    name: "Open the Settings tab and navigate to API keys",
    text: "Inside Ads Manager, click the Settings tab in the left navigation, then locate the API keys section. Each API key is scoped to one ad account; if you operate multiple ad accounts you will need a separate key per account.",
  },
  {
    name: "Click Create new key and copy the value immediately",
    text: "Click Create new key, name it descriptively (e.g. 'Main account, production'), and copy the value. The key is shown only once at creation. If you do not copy it now, you have to generate a new key (the original key stays valid but unrecoverable).",
  },
  {
    name: "Test the key with GET /ad_account",
    text: "Run `curl -H \"Authorization: Bearer $OPENAI_ADS_API_KEY\" https://api.ads.openai.com/v1/ad_account`. A successful response returns the ad account's ID, name, URL, status, timezone, currency, and brand review status. A 401 Unauthorized response means the key is wrong or is not an Ads API key (a platform.openai.com key fails here).",
  },
];

// ── FAQs ──
const faqs = [
  {
    q: "Is the OpenAI Ads API key the same as my OpenAI platform API key?",
    a: "No. They are two different credentials issued by two different OpenAI products. The OpenAI Ads API key (OPENAI_ADS_API_KEY) is provisioned in the Settings tab of ads.openai.com and authenticates requests to the Advertiser API and Insights API at https://api.ads.openai.com/v1/. The standard OPENAI_API_KEY is provisioned at platform.openai.com and authenticates chat completion, embedding, image generation, and other model API requests at https://api.openai.com/v1/. They cannot be used interchangeably. A request to the Advertiser API with a platform.openai.com key returns 401 Unauthorized.",
  },
  {
    q: "Where in Ads Manager do I find the API key?",
    a: "Sign in to ads.openai.com, click the Settings tab in the left navigation, and look for the API keys section. Click Create new key, name it, and copy the value. The key is shown only once at creation. If you do not copy it now, generate a new key (the old one stays valid but the value is unrecoverable).",
  },
  {
    q: "Can one API key manage multiple ad accounts?",
    a: "No. Each OpenAI Ads API key is scoped to exactly one ad account. Agencies managing multiple client accounts, or enterprises with separate business units, need a separate key per account: OpenAI's API Partner Setup guide tells partners to use the API key associated with each client ad account they configure. Run GET /ad_account with each key to confirm which account it belongs to before you change anything.",
  },
  {
    q: "What can I do with the OpenAI Ads API key?",
    a: "Manage and report on one ad account in code. OpenAI describes the Advertiser API as the way to create and manage campaigns, ad groups, ads, product feeds, conversion tracking, and reporting. That covers campaigns, ad groups, and ads (including previews), creative image uploads, custom audiences, product feeds, bulk jobs, audit logs, and conversion setup (pixels, Conversions API keys, and event settings). The Insights endpoints report impressions, clicks, spend, CTR, CPC, and CPM at four scopes (ad_account, campaign, ad_group, ad), and conversions are now documented outputs too: conversions, cost per action (CPA), post-click conversion rate, attributed sales, and ROAS, with selectable click and view attribution windows. Conversion events themselves still reach OpenAI through the Measurement Pixel or the Conversions API, which uses its own Conversions API key rather than this one.",
  },
  {
    q: "What happens if I lose my OpenAI Ads API key?",
    a: "The key value is shown only once at creation. If you lose the value, the key itself stays valid in OpenAI's system but you cannot recover the secret. The fix: generate a new key in the Settings tab, swap it into your environment (env var, secrets manager, or the AI-Advisors ChatGPT Ads integration field), then revoke the old key. Treat the key like a password: never commit it to git, never paste it into a public document, and rotate it on a schedule.",
  },
  {
    q: "What rate limits does the OpenAI Ads API enforce?",
    a: "Two limits enforced together: 600 requests per minute per endpoint, and 1,200 requests per minute overall per ad account. Both are also enforced by IP address, so a request must stay within all four ceilings (per-endpoint per-account, overall per-account, per-endpoint per-IP, overall per-IP) simultaneously. Creating bulk jobs has its own limit of 10 requests per 10 seconds per ad account. For most marketing workflows the limits are not constraining, but bulk operations like backfilling six months of campaign-level insights can hit the limit if you do not paginate carefully.",
  },
];

function Content() {
  return (
    <article>
      {/* ── Featured-snippet box ── */}
      <div
        style={{
          margin: "0 0 32px",
          padding: "20px 24px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <P>
          <Strong>
            The <A href="/glossary/openai-ads-api-key">OpenAI Ads API key</A> (<Code>OPENAI_ADS_API_KEY</Code>) is separate from your <Code>OPENAI_API_KEY</Code> for chat completion. Provision it at ads.openai.com &rarr; Settings &rarr; API keys, copy the value at creation (shown only once), test it via <Code>GET /ad_account</Code>, then use it to manage campaigns and pull performance data programmatically. Two paths: the AI-Advisors ChatGPT Ads integration (paste your key, view / edit / publish / analyze inside AI-Advisors with AEO overlay) or the manual curl walkthrough below.
          </Strong>
        </P>
        <ul style={{ paddingLeft: 20, margin: "0 0 0" }}>
          <Li><Strong>The key is NOT the same as your platform.openai.com key.</Strong> Different env var, different base URL, different provisioning UI</Li>
          <Li><Strong>Provisioned at:</Strong> ads.openai.com &rarr; Settings &rarr; API keys</Li>
          <Li><Strong>Scope:</Strong> One key per ad account (agencies use each client account&#39;s own key)</Li>
          <Li><Strong>Base URL:</Strong> <Code>https://api.ads.openai.com/v1/</Code></Li>
          <Li><Strong>Test endpoint:</Strong> <Code>GET /ad_account</Code></Li>
          <Li><Strong>Rate limits:</Strong> 600 req/min per endpoint, 1,200 req/min per ad account (plus per-IP limits)</Li>
        </ul>
      </div>

      {/* ── Intro ── */}
      <P>
        OpenAI launched its <A href="/glossary/chatgpt-ads">ChatGPT Ads</A> platform on February 9, 2026 and opened the Ads Manager to every US advertiser on May 5, 2026 with no account minimum. Alongside the May 5 expansion, OpenAI shipped the <Strong>Advertiser API</Strong>, and it has grown since. It now covers campaign management (campaigns, ad groups, ads, and creative files, plus custom audiences, product feeds, and conversion setup) and performance reporting through its <Strong>Insights</Strong> endpoints, which return conversions, CPA, and ROAS alongside impressions, clicks, and spend. All of it authenticates with one credential: the OpenAI Ads API key.
      </P>
      <P>
        The single most common mistake when developers and marketers first encounter this API is confusing the <Code>OPENAI_ADS_API_KEY</Code> with the standard <Code>OPENAI_API_KEY</Code> used for chat completion. They are two completely separate credentials, issued by two completely separate OpenAI products, with two different base URLs. This guide leads with the disambiguation and then walks both install paths: the AI-Advisors ChatGPT Ads integration (2 minutes, no curl) and the manual curl walkthrough (5 minutes, full control).
      </P>

      <PullQuote>
        The OpenAI Ads API key is not your platform.openai.com key. Different env var, different base URL, different provisioning UI. Confusing the two returns 401 in both directions.
      </PullQuote>

      {/* ── What it is and what it isn't ── */}
      <H2 id="what-it-is">What the OpenAI Ads API key is (and isn&#39;t)</H2>
      <P>
        The OpenAI Ads API key is a bearer token that authenticates programmatic requests to OpenAI&#39;s advertising platform. Read the <A href="https://developers.openai.com/ads/api-overview">official API overview</A> for the full reference; the table below isolates the differences from the chat completion key that almost everyone confuses it with on first encounter.
      </P>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", margin: "16px 0 28px" }}>
        <table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse", fontFamily: "var(--body)", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEXT_DIM, fontWeight: 600 }}>Dimension</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontWeight: 700 }}>OpenAI Ads API key</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontWeight: 700 }}>OpenAI platform API key</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <td style={{ padding: "12px", color: TEXT, fontWeight: 600 }}>Env-var name</td>
              <td style={{ padding: "12px", color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 13 }}>OPENAI_ADS_API_KEY</td>
              <td style={{ padding: "12px", color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 13 }}>OPENAI_API_KEY</td>
            </tr>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <td style={{ padding: "12px", color: TEXT, fontWeight: 600 }}>Provisioned at</td>
              <td style={{ padding: "12px", color: TEXT_DIM }}>ads.openai.com &rarr; Settings &rarr; API keys</td>
              <td style={{ padding: "12px", color: TEXT_DIM }}>platform.openai.com &rarr; API keys</td>
            </tr>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <td style={{ padding: "12px", color: TEXT, fontWeight: 600 }}>Base URL</td>
              <td style={{ padding: "12px", color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 13 }}>https://api.ads.openai.com/v1/</td>
              <td style={{ padding: "12px", color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 13 }}>https://api.openai.com/v1/</td>
            </tr>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <td style={{ padding: "12px", color: TEXT, fontWeight: 600 }}>Scope</td>
              <td style={{ padding: "12px", color: TEXT_DIM }}>One ad account per key</td>
              <td style={{ padding: "12px", color: TEXT_DIM }}>Project or organization, multiple permissions</td>
            </tr>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <td style={{ padding: "12px", color: TEXT, fontWeight: 600 }}>What it can do</td>
              <td style={{ padding: "12px", color: TEXT_DIM }}>Manage campaigns, ads, creatives, audiences, and conversion setup; read delivery and conversion reporting</td>
              <td style={{ padding: "12px", color: TEXT_DIM }}>Generate completions, embeddings, images, fine-tunes</td>
            </tr>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <td style={{ padding: "12px", color: TEXT, fontWeight: 600 }}>Test endpoint</td>
              <td style={{ padding: "12px", color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 13 }}>GET /ad_account</td>
              <td style={{ padding: "12px", color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 13 }}>GET /models</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Prerequisites ── */}
      <H2 id="prerequisites">Prerequisites</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>An Ads Manager account.</Strong> Sign in at <A href="https://ads.openai.com">ads.openai.com</A>. Self-serve has been open to every US advertiser since May 5, 2026 with no account minimum, and OpenAI documents a per-campaign daily minimum that varies by billing currency (US $25/day, September 2026).</Li>
        <Li><Strong>Admin access on the ad account.</Strong> Ads Manager accounts have three roles: Admin, Member, and Viewer. Viewers have no write permissions, so the simplest path is to have an account Admin create the key. A ChatGPT workspace or API Platform role does not carry over; OpenAI grants Ads permissions separately.</Li>
        <Li><Strong>An idea of which environment you are using.</Strong> Generate separate keys for development and production. Two keys, two ad accounts, no chance of test traffic hitting production billing.</Li>
        <Li><Strong>A secrets manager or environment-variable strategy.</Strong> The key is a credential; treat it like a password. We&#39;ll cover storage best practices below.</Li>
      </ul>

      {/* ── PATH A: AI-Advisors integration ── */}
      <H2 id="ai-advisors-integration">One-click connection: AI-Advisors ChatGPT Ads integration (2 minutes)</H2>
      <P>
        The fastest path is the <A href="/integrations/chatgpt-ads">AI-Advisors ChatGPT Ads integration</A>. Paste your <Code>OPENAI_ADS_API_KEY</Code> into the integration form and AI-Advisors uses the key to surface ChatGPT Ads campaign performance (impressions, clicks, spend, CTR, CPC, CPM) inside the AI-Advisors dashboard alongside your organic AI citation share, GA4, and Google Search Console data. The integration also enables view / edit / publish operations on campaigns and ad groups directly from inside AI-Advisors with the AEO overlay you cannot get anywhere else.
      </P>
      <ScreenshotFrame
        src="/screenshots/ai-ads/ai-advisors-chatgpt-ads-overview-dashboard.png"
        title="AI Ads / Overview"
        alt="AI-Advisors AI Ads overview for ChatGPT Ads, with impressions, clicks, CTR, spend, average CPC and CPM tiles above a daily performance chart."
        caption="Impressions, clicks, CTR, and spend on one screen, with a daily performance chart."
      />
      <P>
        Included on the Growth plan and above. The key is encrypted at rest and never leaves AI-Advisors. You can revoke access at any time by deleting the key from the integration form or rotating it in OpenAI&#39;s Ads Manager.
      </P>

      <InlineCTA href="/integrations/chatgpt-ads" label="Connect the ChatGPT Ads integration">
        Skip the manual curl walkthrough. Paste your $OPENAI_ADS_API_KEY into AI-Advisors and view, edit, publish, and analyze your ChatGPT Ads alongside your AEO data. Included on the Growth plan and above.
      </InlineCTA>

      {/* ── PATH B: Manual provisioning ── */}
      <H2 id="manual-provisioning">Manual: Provision the key at ads.openai.com (5 minutes)</H2>
      <P>
        If you are building your own integration or want to test the API directly with curl before connecting AI-Advisors, the manual provisioning path takes four steps. The key value is shown only once at creation, so read all four steps before clicking Create.
      </P>

      <StepHeader id="step-1" num="1" title="Sign in to ads.openai.com" time="~30 sec" />
      <P>
        Open <A href="https://ads.openai.com">ads.openai.com</A> in your browser and sign in with the account that has admin access to your ad account. This is a different surface from platform.openai.com (the chat completion platform) and from chatgpt.com (the consumer product). Make sure you are on the Ads Manager interface before continuing.
      </P>

      <StepHeader id="step-2" num="2" title="Open the Settings tab and navigate to API keys" time="~1 min" />
      <P>
        In Ads Manager, click <Strong>Settings</Strong> in the left navigation, then locate the <Strong>API keys</Strong> section. The interface lists existing keys (if any) with their name, creation date, and last-used timestamp. The value itself is never shown after creation, so the list is for management (rotation, revocation) rather than retrieval.
      </P>

      <StepHeader id="step-3" num="3" title="Click Create new key and copy the value immediately" time="~1 min" />
      <P>
        Click <Strong>Create new key</Strong>, name it descriptively (e.g. <Code>main-account-prod</Code> or <Code>backfill-script-dev</Code>), and copy the value the moment it appears. The key is shown <Strong>only once at creation</Strong>. Paste it into your secrets manager, env-var file, or password manager before closing the modal.
      </P>
      <Callout kind="warn" title="If you lose the key value">
        <P>
          The key itself stays valid in OpenAI&#39;s system, but you cannot recover the secret. Generate a new key, swap it into your environment, then revoke the old one. This protects against accidental leaks even when you forget to delete the unrecoverable original.
        </P>
      </Callout>

      <StepHeader id="step-4" num="4" title="Test the key with GET /ad_account" time="~30 sec" />
      <P>
        Export the key as an environment variable and run a curl request against the canonical test endpoint:
      </P>
      <CodeBlock lang="Bash" syntax="bash">{`export OPENAI_ADS_API_KEY="<paste the value from step 3>"

curl -H "Authorization: Bearer $OPENAI_ADS_API_KEY" \\
  -H "Accept: application/json" \\
  https://api.ads.openai.com/v1/ad_account`}</CodeBlock>
      <P>
        A successful response returns the ad account&#39;s ID, name, URL, status, timezone, currency, and brand review status. OpenAI recommends checking the currency, timezone, account status, and reviews before you create a campaign, because an account marked active can still have a review outstanding. A 401 Unauthorized response means the key is wrong (typo, expired, revoked) or you are pointing at the wrong base URL (likely <Code>api.openai.com</Code> instead of <Code>api.ads.openai.com</Code>).
      </P>

      {/* ── What the Insights API exposes ── */}
      <H2 id="insights-api">What the Insights API exposes</H2>
      <P>
        Once the key is verified, the <A href="https://developers.openai.com/ads/api-reference/insights">Insights API</A> gives you performance data across four scopes (ad_account / campaign / ad_group / ad). Delivery metrics are impressions, clicks, spend, CTR, CPC, and CPM. Conversions are documented outputs as well: <Code>conversions</Code> (goal conversions from clicks plus views), <Code>cpa</Code> (spend divided by conversions), <Code>post_click_cvr</Code>, attributed sales, and ROAS. OpenAI added its conversion setup and reporting endpoints on June 3, 2026 and selectable attribution windows on September 30, 2026.
      </P>
      <P>
        The windows are a 7, 14, or 30-day click window (default 30) and a 0 or 1-day view window (default 1). They change what reporting counts, not your campaign&#39;s conversion goal or optimization settings, and they leave impressions, clicks, and spend untouched. The conversions themselves still reach OpenAI through the <A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">Measurement Pixel</A> or the Conversions API; Insights is where you read them back.
      </P>
      <CodeBlock lang="Bash" syntax="bash">{`# Daily account-level delivery and conversions for one week.
# Conversion metrics need full days in your ad account's timezone
# (GET /ad_account returns it).
curl -G "https://api.ads.openai.com/v1/ad_account/insights" \\
  -H "Authorization: Bearer $OPENAI_ADS_API_KEY" \\
  --data-urlencode 'aggregation_level=ad_account' \\
  --data-urlencode 'time_granularity=daily' \\
  --data-urlencode 'time_ranges[]={"type":"date_range","since":"2026-10-01","until":"2026-10-07","timezone":"America/New_York"}' \\
  --data-urlencode 'fields[]=readable_time' \\
  --data-urlencode 'fields[]=impressions' \\
  --data-urlencode 'fields[]=clicks' \\
  --data-urlencode 'fields[]=spend' \\
  --data-urlencode 'fields[]=conversions' \\
  --data-urlencode 'fields[]=cpa'`}</CodeBlock>
      <P>
        Pagination uses cursor-based <Code>before</Code> and <Code>after</Code> parameters, with up to 2,000 rows per page. <Code>time_granularity</Code> accepts <Code>hourly</Code>, <Code>daily</Code> (the default), <Code>monthly</Code>, or <Code>none</Code>; <Code>aggregation_level</Code> can be set to <Code>ad_account</Code>, <Code>campaign</Code>, <Code>ad_group</Code>, or <Code>ad</Code> to control the row shape of the response. Requests for conversion metrics can span at most 365 days. For a focused conversion report, <Code>POST /v1/conversions/insights</Code> splits goal conversions into click-through and view-through counts, with optional per-event detail.
      </P>

      {/* ── Object status model ── */}
      <H2 id="object-status">Object status model</H2>
      <P>
        For an ad to actually show to users, every level of the hierarchy has to be ready. OpenAI&#39;s troubleshooting guide checks four levels:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>The ad account is active.</Strong> Its brand review is complete and no spend limit is exhausted.</Li>
        <Li><Strong>The campaign is active.</Strong> It is within its schedule and has budget left. A paused campaign stops an active ad from serving.</Li>
        <Li><Strong>The ad group is active.</Strong> Pausing an ad group stops all of its ads, whatever their own status. Toggling state via the Advertiser API is one of the most common operations.</Li>
        <Li><Strong>The ad is active and approved.</Strong> Monitor the <Code>review_status</Code> field on each ad. An ad with status <Code>active</Code> and review status <Code>in_review</Code> is enabled but cannot serve until the review passes, and editing its creative starts a new review.</Li>
      </ul>
      <ScreenshotFrame
        src="/screenshots/ai-ads/ai-advisors-chatgpt-ads-campaign-manager.png"
        title="AI Ads / Campaigns"
        alt="AI-Advisors AI Ads campaign table for ChatGPT Ads, listing each campaign's status, type, impressions, clicks, CTR, spend and budget."
        caption="Every campaign with status, spend, and per-row on and off switches."
      />

      {/* ── Rate limits + multi-account ── */}
      <H2 id="rate-limits">Rate limits and multi-account access</H2>
      <P>
        The Advertiser API enforces two limits simultaneously:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>600 requests per minute per endpoint</Strong></Li>
        <Li><Strong>1,200 requests per minute overall per ad account</Strong></Li>
      </ul>
      <P>
        Both are also enforced by IP address, so a single request must stay within all four ceilings (per-endpoint per-account, overall per-account, per-endpoint per-IP, overall per-IP) at the same time. Creating bulk jobs has a separate limit of 10 requests per 10 seconds per ad account. For most marketing workflows the limits are not constraining; bulk operations like backfilling six months of campaign-level insights can hit the cap if you do not paginate carefully.
      </P>
      <P>
        Each API key is scoped to <Strong>one ad account</Strong>. Agencies managing multiple client accounts and enterprises with separate business units need one key per ad account; OpenAI&#39;s <A href="https://developers.openai.com/ads/api-partner-setup">API Partner Setup guide</A> tells partners to use the key associated with each client account and to confirm it with <Code>GET /ad_account</Code> before configuring anything. Some partner capabilities, such as pixel management, Conversions API key creation, and conversion-optimized bidding, must also be enabled for the client account; if those calls return 403 or 404, the guide says to contact your OpenAI partner representative.
      </P>

      {/* ── Security ── */}
      <H2 id="security">Security best practices</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Store as an environment variable.</Strong> Never hard-code the key in source. Use <Code>process.env.OPENAI_ADS_API_KEY</Code> (Node), <Code>os.environ[&quot;OPENAI_ADS_API_KEY&quot;]</Code> (Python), or your platform&#39;s secrets manager (AWS Secrets Manager, Doppler, 1Password Connect, Vercel encrypted env vars).</Li>
        <Li><Strong>Keep <Code>.env</Code> in <Code>.gitignore</Code>.</Strong> Committing the key to a public or private repo creates a permanent leak. Use <Code>git-secrets</Code> or a pre-commit hook to prevent accidental commits.</Li>
        <Li><Strong>Rotate on a schedule.</Strong> Most teams rotate every 90 days, plus immediately if there is any suspicion the key has been exposed (departing employee, accidental commit, third-party breach, agency offboarding).</Li>
        <Li><Strong>Separate development and production keys.</Strong> One key per environment, scoped to its own ad account. Test traffic should never hit production billing.</Li>
        <Li><Strong>Restrict by IP if your stack supports it.</Strong> Most secrets-manager solutions support source-IP allowlisting at the runtime layer; combined with the API&#39;s per-IP rate limit, this provides defense in depth.</Li>
      </ul>

      {/* ── Common mistakes ── */}
      <H2 id="common-mistakes">Common mistakes</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Confusing with platform.openai.com keys.</Strong> The #1 mistake. The chat completion key (<Code>OPENAI_API_KEY</Code>) cannot authenticate Advertiser API requests; the Ads key cannot authenticate chat completion. Both fail with 401.</Li>
        <Li><Strong>Pointing at the wrong base URL.</Strong> Use <Code>https://api.ads.openai.com/v1/</Code> for the Ads API. The platform key&#39;s base URL is <Code>https://api.openai.com/v1/</Code>.</Li>
        <Li><Strong>Not copying the value at creation.</Strong> The key is shown only once. If you close the modal without copying, generate a new key (the original stays valid but unrecoverable).</Li>
        <Li><Strong>Assuming one key covers several ad accounts.</Strong> Each key is scoped to exactly one ad account, so every request acts on that account. Run <Code>GET /ad_account</Code> to confirm which account a key belongs to before you change anything.</Li>
        <Li><Strong>Sending conversion events with the Ads API key.</Strong> The Conversions API uses its own Conversions API key and a separate host, not <Code>OPENAI_ADS_API_KEY</Code>. The Ads API key can create Conversions API keys for accounts where that is enabled, but it does not send events.</Li>
        <Li><Strong>Forgetting that ads must pass review.</Strong> Even if the ad, ad group, and campaign are all active, an ad whose <Code>review_status</Code> is still <Code>in_review</Code> will not serve.</Li>
      </ul>

      {/* ── Pair with the pixel ── */}
      <H2 id="pair-with-pixel">Pair with the Measurement Pixel for end-to-end tracking</H2>
      <P>
        The API key handles the management and reporting side of your campaigns. For the measurement side (conversions back from your site to OpenAI), you also need the JavaScript Measurement Pixel installed via Google Tag Manager, the Conversions API, or both. Once events flow, the same Ads API key reads the results back through Insights. See our companion post on <A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">how to install the ChatGPT Ads Measurement Pixel via GTM</A>. For the full strategic picture of why both the pixel and the API key matter, see our deep-dive on <A href="/blog/chatgpt-ads-conversion-tracking">ChatGPT Ads conversion tracking</A>.
      </P>

      <PullQuote>
        The API key manages and reports. The pixel measures and attributes. Both run from the same ad account; both belong in your stack.
      </PullQuote>

      <InlineCTA href="/integrations/chatgpt-ads" label="Connect the ChatGPT Ads integration">
        Skip the manual provisioning, secrets-manager setup, and per-endpoint plumbing. Paste your $OPENAI_ADS_API_KEY into AI-Advisors and view, edit, publish, and analyze your ChatGPT Ads campaigns overlayed with your AEO data.
      </InlineCTA>

      {/* ── HowTo + FAQPage JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to get an OpenAI Ads API key for ChatGPT Ads",
            description:
              "Provision an OpenAI Ads API key (OPENAI_ADS_API_KEY) at ads.openai.com Settings, then test it via GET /ad_account at https://api.ads.openai.com/v1/. The key authenticates the Advertiser API and Insights API, scoped to one ad account per key.",
            totalTime: "PT5M",
            step: howToSteps.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.name,
              text: s.text,
              url: `https://www.ai-advisors.ai/blog/how-to-get-openai-ads-api-key#step-${i + 1}`,
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.a,
              },
            })),
          }),
        }}
      />

      {/* ── FAQ section ── */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* ── Related reading ── */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">How to install the ChatGPT Ads Measurement Pixel via Google Tag Manager</A></Li>
        <Li><A href="/blog/chatgpt-ads-conversion-tracking">ChatGPT Ads Conversion Tracking Is Real: What OpenAI&#39;s April 30 Privacy Policy Update Tells You About the Roadmap</A></Li>
        <Li><A href="/blog/how-to-report-on-chatgpt-ad-campaigns">How to Report on ChatGPT Ad Campaigns: A 2026 Measurement Stack</A></Li>
        <Li><A href="/blog/how-to-use-chatgpt-ads-manager">How to Use ChatGPT Ads: A Marketer&#39;s Step-by-Step Guide (2026)</A></Li>
        <Li><A href="/blog/chatgpt-ads-complete-guide">What Are ChatGPT Ads? The Complete Guide for 2026</A></Li>
        <Li><A href="/blog/how-to-measure-roi-for-chatgpt-ads">How to Measure ROI for ChatGPT Ads: The 2026 ROAS Framework for B2B Marketers</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "how-to-get-openai-ads-api-key",
  title: "How to get an OpenAI Ads API key for ChatGPT Ads",
  seoTitle: "How to get an OpenAI Ads API key for ChatGPT Ads",
  excerpt:
    "The OpenAI Ads API key (OPENAI_ADS_API_KEY) is separate from your OPENAI_API_KEY used for chat completion. Provision it at ads.openai.com Settings, test via GET /ad_account, and use it to manage campaigns and pull performance via the Advertiser and Insights APIs. Two paths: the AI-Advisors ChatGPT Ads integration (paste your key, view edit publish analyze inside AI-Advisors) or the manual 4-step curl walkthrough.",
  metaDescription:
    "Provision an OpenAI Ads API key at ads.openai.com Settings. Test via GET /ad_account. Authenticates Advertiser API + Insights API. Disambiguation table.",
  date: "2026-05-13",
  lastUpdated: "2026-10-17",
  readTime: "12 min read",
  category: "AI Ads",
  tags: ["OpenAI Ads API key", "OPENAI_ADS_API_KEY", "Advertiser API", "Insights API", "ChatGPT Ads", "API authentication", "HowTo"],
  Content,
};

export default post;
